import { scale030 } from '@xing-com/tokens';

import {
  BUTTON_PADDING,
  BUTTON_MARGIN_RIGHT,
  FONT_FAMILY,
  FONT_SIZES,
  FONT_WEIGHT,
  ICON_MARGIN_RIGHT,
  ICON_SIZES,
  MINI_BADGE_MARGIN_RIGHT,
} from '../constants';
import type { ContentSwitcherSize } from '../content-switcher';

import { clampButtonText } from './clamp-button-text';

type CalculateButtonWidthArguments = {
  copy: React.ReactNode;
  size: ContentSwitcherSize;
  hasIcon: boolean;
  hasMiniBadge: boolean;
};

type MeasureTextWidthArguments = Omit<
  CalculateButtonWidthArguments,
  'hasIcon' | 'hasMiniBadge'
>;

const toTwoDecimals = (value: number): number =>
  Number(parseFloat(value.toString()).toFixed(2));

const measureTextWidth = ({
  copy,
  size,
}: MeasureTextWidthArguments): number => {
  if (!copy) return 0;

  const canvas = document.createElement('canvas');
  if (!canvas) return 0;

  const context = canvas.getContext('2d');
  if (!context) return 0;

  context.font = `${FONT_WEIGHT} ${FONT_SIZES[size]} ${FONT_FAMILY}`;
  const stringCopy = copy.toString();
  const clampedCopy = clampButtonText(stringCopy);
  const width = context.measureText(clampedCopy).width;

  return toTwoDecimals(width);
};

export const calculateButtonWidth = ({
  copy,
  size,
  hasIcon,
  hasMiniBadge,
}: CalculateButtonWidthArguments): number => {
  const iconMarginRight = toTwoDecimals(parseFloat(ICON_MARGIN_RIGHT));
  const miniBadgeMarginRight = toTwoDecimals(
    parseFloat(MINI_BADGE_MARGIN_RIGHT)
  );
  const miniBadgeWidth = toTwoDecimals(parseFloat(scale030));
  const textWidth = measureTextWidth({ copy, size });
  const paddingWidth = toTwoDecimals(parseFloat(BUTTON_PADDING) * 2);
  const buttonMarginRight = toTwoDecimals(parseFloat(BUTTON_MARGIN_RIGHT));
  const iconWidth = hasIcon
    ? toTwoDecimals(ICON_SIZES[size] + iconMarginRight)
    : 0;
  const badgeWidth = hasMiniBadge
    ? toTwoDecimals(miniBadgeMarginRight + miniBadgeWidth)
    : 0;

  const totalWidth =
    textWidth + paddingWidth + iconWidth + badgeWidth + buttonMarginRight;

  return toTwoDecimals(totalWidth);
};
